import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { safeHtml } from "@/lib/sanitize";
import { normalizeDescription } from "@/lib/jobNorm";
import api, { formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";

const EMPTY_JOB = {
    title: "", company: "", location: "", salary: "",
    employment_type: "Full-time", industry: "General",
    description: "", requirements: "",
    is_active: true,
};

const TYPES = ["Full-time", "Part-time", "Contract", "Temporary", "Internship"];

export const JobEditorDialog = ({ open, onOpenChange, editing, onSaved }) => {
    const [form, setForm] = useState(EMPTY_JOB);
    const [saving, setSaving] = useState(false);
    // Live preview — mirrors backend normalize_description. Cheap enough at
    // typical description sizes that we skip debouncing.
    const previewHtml = useMemo(() => normalizeDescription(form.description || ""), [form.description]);

    useEffect(() => {
        if (!open) return;
        setForm(editing ? {
            title: editing.title || "",
            company: editing.company || "",
            location: editing.location || "",
            salary: editing.salary || "",
            employment_type: editing.employment_type || "Full-time",
            industry: editing.industry || "General",
            description: editing.description || "",
            requirements: (editing.requirements || []).join("\n"),
            is_active: editing.is_active !== false,
        } : EMPTY_JOB);
    }, [open, editing]);

    const saveJob = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
        };
        try {
            setSaving(true);
            if (editing) {
                await api.put(`/admin/jobs/${editing.id}`, payload);
                toast.success("Job updated.");
            } else {
                await api.post("/admin/jobs", payload);
                toast.success("Job created.");
            }
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-brand-navy dark:text-slate-100">{editing ? "Edit job" : "Create new job"}</DialogTitle>
                    <DialogDescription>{editing ? "Update the job listing." : "This job will appear publicly on Simonara. The preview panel on the right shows exactly how the description will render post-normalization."}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
                    <form onSubmit={saveJob} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Label>Title</Label>
                        <Input data-testid={TID.admin.jobTitle} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                        <Label>Company</Label>
                        <Input data-testid={TID.admin.jobCompany} required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                        <Label>Location</Label>
                        <Input data-testid={TID.admin.jobLocation} required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                        <Label>Salary</Label>
                        <Input data-testid={TID.admin.jobSalary} value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                        <Label>Industry</Label>
                        <Input data-testid={TID.admin.jobIndustry} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-2" />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Employment type</Label>
                        <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                            <SelectTrigger data-testid={TID.admin.jobType} className="mt-2">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Label>Description</Label>
                        <Textarea data-testid={TID.admin.jobDescription} rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Requirements (one per line)</Label>
                        <Textarea data-testid={TID.admin.jobRequirements} rows={4} value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} className="mt-2" />
                    </div>
                        <DialogFooter className="md:col-span-2">
                            <button type="submit" disabled={saving} data-testid={TID.admin.jobSave} className="btn-navy disabled:opacity-60">
                                {saving ? <><Loader2 size={18} className="mr-2 animate-spin" /> Saving...</> : "Save job"}
                            </button>
                        </DialogFooter>
                    </form>
                    {/* Live preview — mirrors backend normalize_description
                        so admins see the exact publish-time layout while
                        typing. Sticks to the top-right so it stays visible
                        while the form scrolls. */}
                    <aside
                        className="hidden lg:block sticky top-2 self-start card-soft p-4 max-h-[80vh] overflow-y-auto"
                        data-testid="admin-job-preview-panel"
                    >
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1.5 mb-3">
                            <Eye size={11} /> Live preview
                        </div>
                        <div className="text-[9px] uppercase tracking-wider font-mono font-semibold text-muted-foreground mb-1.5">
                            Req · <span className="opacity-60">SIM-YYYY-NNNN (assigned on save)</span>
                        </div>
                        <h2 className="text-xl font-heading font-bold text-foreground leading-tight" data-testid="admin-job-preview-title">
                            {form.title || <span className="opacity-40 italic font-normal text-base">Job title…</span>}
                        </h2>
                        <div className="mt-1 text-xs text-muted-foreground">
                            {[form.company, form.location].filter(Boolean).join(" · ") || <span className="opacity-40 italic">Company · Location</span>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {form.employment_type && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-700">
                                    {form.employment_type}
                                </span>
                            )}
                            {form.industry && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-500/30 bg-slate-500/10 text-slate-700">
                                    {form.industry}
                                </span>
                            )}
                            {form.salary && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700">
                                    {form.salary}
                                </span>
                            )}
                        </div>
                        <div className="mt-4 border-t border-border pt-3">
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Description (as it will render)</div>
                            {form.description.trim() ? (
                                <div
                                    className="prose prose-sm dark:prose-invert max-w-none"
                                    data-testid="admin-job-preview-description"
                                    dangerouslySetInnerHTML={{ __html: safeHtml(previewHtml) }}
                                />
                            ) : (
                                <div className="text-xs italic opacity-50">Start typing to see how the description will render on the public job page…</div>
                            )}
                        </div>
                        {form.requirements.trim() && (
                            <div className="mt-4 border-t border-border pt-3">
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Requirements</div>
                                <ul className="text-sm list-disc pl-5 space-y-1 text-foreground">
                                    {form.requirements.split(/\n+/).filter((l) => l.trim()).map((l, i) => <li key={i}>{l.trim()}</li>)}
                                </ul>
                            </div>
                        )}
                        <p className="mt-4 text-[10px] text-muted-foreground italic border-t border-border pt-3">
                            Server-side normalization strips inline styles + `&lt;script&gt;` tags and wraps plain-text blocks in `&lt;p&gt;` — this preview matches that output.
                        </p>
                    </aside>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default JobEditorDialog;
